import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  addReplaceAliasForTerminologyItem,
  deleteTerminologyItem,
  getTerminologyItem,
} from "@/api/terminology";
import { useToast } from "@/components/toast";

import { SenseSearch } from "../SenseSearch";
import type { SenseTarget } from "../senseSearchTypes";

export function ForbiddenFormPanel() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [picked, setPicked] = useState<SenseTarget | null>(null);
  const [aliasText, setAliasText] = useState("");

  const detail = useQuery({
    queryKey: ["terminology-item", picked?.item_id],
    queryFn: () => getTerminologyItem(picked!.item_id),
    enabled: !!picked,
  });
  const forbidden = detail.data?.ok ? (detail.data.data.replace_aliases ?? []) : [];
  const canonical = picked?.target_abbreviation || picked?.target_text || "";

  function refetch() {
    void qc.invalidateQueries({ queryKey: ["terminology-item", picked?.item_id] });
  }

  const addMut = useMutation({
    mutationFn: async () => {
      const r = await addReplaceAliasForTerminologyItem(picked!.item_id, {
        alias_text: aliasText.trim(),
        canonical_text: canonical,
        exact: false,
      });
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: () => {
      toast("Форма запрещена", "success");
      setAliasText("");
      refetch();
    },
    onError: (e: Error) => toast(e.message, "error"),
  });
  const delMut = useMutation({
    mutationFn: async (aliasItemId: string) => {
      const r = await deleteTerminologyItem(aliasItemId);
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: () => {
      toast("Запрет снят", "success");
      refetch();
    },
    onError: (e: Error) => toast(e.message, "error"),
  });

  return (
    <div>
      <SenseSearch onPick={setPicked} pickedItemId={picked?.item_id} />
      {picked && (
        <div style={{ marginTop: 12 }}>
          <div className="mt-muted" style={{ marginBottom: 6 }}>
            Правильный перевод: <b>{canonical || "—"}</b>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              marginBottom: 8,
            }}
          >
            <div className="mt-muted">Уже запрещено:</div>
            {detail.isLoading ? (
              <span className="mt-muted" style={{ fontSize: "var(--fs-sm)" }}>
                загружаю…
              </span>
            ) : forbidden.length === 0 ? (
              <span className="mt-muted" style={{ fontSize: "var(--fs-sm)" }}>
                пока ничего
              </span>
            ) : (
              forbidden.map((f) => (
                <div
                  key={f.item_id ?? f.alias_text}
                  className="rf-example"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: 6,
                    fontSize: "var(--fs-sm)",
                  }}
                >
                  <span style={{ flex: 1 }}>
                    «{f.alias_text}» → «{f.canonical_text}»
                  </span>
                  {f.item_id && (
                    <button
                      type="button"
                      className="mt-btn mt-btn--sm mt-btn--danger mt-btn--ghost"
                      onClick={() => delMut.mutate(f.item_id!)}
                    >
                      убрать
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
          <div
            style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}
          >
            <input
              className="mt-input mt-input--sm"
              placeholder="неправильная форма (напр. ППАК)"
              value={aliasText}
              onChange={(e) => setAliasText(e.target.value)}
              style={{ minWidth: 240 }}
            />
            <button
              type="button"
              className="mt-btn mt-btn--sm mt-btn--primary"
              disabled={!aliasText.trim() || !canonical || addMut.isPending}
              onClick={() => addMut.mutate()}
            >
              Запретить
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
